import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, Modal, Spin, message } from 'antd';
import { 
  getUserSubscription, 
  cancelSubscriptionAutoRenewal, 
  resumeSubscriptionAutoRenewal, 
  cancelSubscription 
} from '@/services/user';
import { UserSubscription, SubscriptionStatus, BillingCycle } from '@/models/common/payment';
import styles from './index.module.scss';
import dayjs from 'dayjs';

interface SubscriptionInfoProps {
  onRefresh?: () => void;
}

const SubscriptionInfo: React.FC<SubscriptionInfoProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState<'cancel' | 'cancelAuto' | 'resumeAuto'>('cancel');

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      const res = await getUserSubscription();
      if (res.data?.subscription) {
        setSubscription(res.data.subscription);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      message.error('获取订阅信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleCancelAutoRenewal = async () => {
    if (!subscription?.publicId) return;
    
    try {
      await cancelSubscriptionAutoRenewal(subscription.publicId);
      message.success('已取消自动续费');
      fetchSubscription();
      if (onRefresh) onRefresh();
      setModalVisible(false);
    } catch (error) {
      console.error('Failed to cancel auto renewal:', error);
      message.error('取消自动续费失败');
    }
  };

  const handleResumeAutoRenewal = async () => {
    if (!subscription?.publicId) return;
    
    try {
      await resumeSubscriptionAutoRenewal(subscription.publicId);
      message.success('已恢复自动续费');
      fetchSubscription();
      if (onRefresh) onRefresh();
      setModalVisible(false);
    } catch (error) {
      console.error('Failed to resume auto renewal:', error);
      message.error('恢复自动续费失败');
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.publicId) return;
    
    try {
      await cancelSubscription(subscription.publicId);
      message.success('已取消订阅');
      fetchSubscription();
      if (onRefresh) onRefresh();
      setModalVisible(false);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      message.error('取消订阅失败');
    }
  };

  const showModal = (action: 'cancel' | 'cancelAuto' | 'resumeAuto') => {
    setModalAction(action);
    setModalVisible(true);
  };

  const getStatusTag = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return <Tag color="green">有效</Tag>;
      case SubscriptionStatus.CANCELED:
        return <Tag color="red">已取消</Tag>;
      case SubscriptionStatus.EXPIRED:
        return <Tag color="gray">已过期</Tag>;
      case SubscriptionStatus.PAYMENT_FAILED:
        return <Tag color="orange">支付失败</Tag>;
      default:
        return <Tag color="default">未知状态</Tag>;
    }
  };

  const getBillingCycleText = (cycle: BillingCycle) => {
    switch (cycle) {
      case BillingCycle.MONTHLY:
        return '月度';
      case BillingCycle.YEARLY:
        return '年度';
      default:
        return '未知';
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD');
  };

  const getModalContent = () => {
    switch (modalAction) {
      case 'cancel':
        return {
          title: '取消订阅',
          content: '确定要取消当前订阅吗？取消后将立即失去相应权益，且不会退还已支付的费用。',
          onOk: handleCancelSubscription
        };
      case 'cancelAuto':
        return {
          title: '取消自动续费',
          content: '确定要取消自动续费吗？取消后，您的订阅将在当前周期结束后自动停止。',
          onOk: handleCancelAutoRenewal
        };
      case 'resumeAuto':
        return {
          title: '恢复自动续费',
          content: '确定要恢复自动续费吗？您的订阅将在当前周期结束后自动续期。',
          onOk: handleResumeAutoRenewal
        };
      default:
        return {
          title: '',
          content: '',
          onOk: () => {}
        };
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin />
      </div>
    );
  }

  if (!subscription) {
    return (
      <Card className={styles.noSubscription}>
        <p>您当前没有有效的订阅</p>
      </Card>
    );
  }

  const { title, content, onOk } = getModalContent();

  return (
    <div className={styles.subscriptionInfo}>
      <Card title="我的订阅" className={styles.subscriptionCard}>
        <div className={styles.infoRow}>
          <div className={styles.label}>订阅产品：</div>
          <div className={styles.value}>{subscription.productCode}</div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.label}>订阅状态：</div>
          <div className={styles.value}>{getStatusTag(subscription.subscriptionStatus)}</div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.label}>计费周期：</div>
          <div className={styles.value}>{getBillingCycleText(subscription.billingCycle)}</div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.label}>开始日期：</div>
          <div className={styles.value}>{formatDate(subscription.startDate)}</div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.label}>结束日期：</div>
          <div className={styles.value}>{formatDate(subscription.endDate)}</div>
        </div>
        {subscription.autoRenew !== undefined && (
          <div className={styles.infoRow}>
            <div className={styles.label}>自动续费：</div>
            <div className={styles.value}>{subscription.autoRenew ? '已开启' : '已关闭'}</div>
          </div>
        )}
        {subscription.nextBillingDate && (
          <div className={styles.infoRow}>
            <div className={styles.label}>下次计费日期：</div>
            <div className={styles.value}>{formatDate(subscription.nextBillingDate)}</div>
          </div>
        )}

        <div className={styles.actions}>
          {subscription.subscriptionStatus === SubscriptionStatus.ACTIVE && (
            <>
              {subscription.autoRenew ? (
                <Button type="default" onClick={() => showModal('cancelAuto')}>
                  取消自动续费
                </Button>
              ) : (
                <Button type="primary" onClick={() => showModal('resumeAuto')}>
                  恢复自动续费
                </Button>
              )}
              <Button danger onClick={() => showModal('cancel')}>
                取消订阅
              </Button>
            </>
          )}
        </div>
      </Card>

      <Modal
        title={title}
        open={modalVisible}
        onOk={onOk}
        onCancel={() => setModalVisible(false)}
        okText="确认"
        cancelText="取消"
      >
        <p>{content}</p>
      </Modal>
    </div>
  );
};

export default SubscriptionInfo; 